import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import React, { useEffect, useRef, useState } from 'react'

export const createDaynStore = (init = 30, key = 'global') =>
  create(
    persist((set) => ({ value: init, update: (value) => set({ value }) }), {
      name: `dayn-storage-${key}`,
      storage: createJSONStorage(() => localStorage),
    }),
  )

export const useDayn = createDaynStore(30)

const daynOptions = [
  { code: 30, name: '30 Days' },
  { code: 60, name: '60 Days' },
  { code: 120, name: '120 Days' },
]

export const DropdownDay = ({ value, update }) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    // Update the input element's value when the external value prop changes
    if (inputRef.current) {
      inputRef.current.value = value === '' ? '' : String(value)
    }
  }, [value])

  const applyInputValueToDayn = () => {
    if (!inputRef.current) return
    const num = parseInt(inputRef.current.value, 10)
    if (!isNaN(num) && num >= 0) {
      update(num)
    } else if (inputRef.current.value === '') {
      update('')
    } else {
      // If invalid input, revert input field to the last valid external value
      inputRef.current.value = value === '' ? '' : String(value)
    }
  }

  const handleInputChange = (e) => {
    e.stopPropagation() // Prevent event from bubbling up and closing dropdown
    // No state update here, using uncontrolled component
  }

  const handleInputBlur = () => {
    applyInputValueToDayn()
  }

  const handleInputKeyDown = (e) => {
    e.stopPropagation() // CRITICAL: Prevent Radix UI typeahead from stealing focus
    if (e.key === 'Enter') {
      applyInputValueToDayn()
      e.currentTarget.blur() // Manually blur the input to close keyboard on mobile
    }
  }

  const handlePresetSelect = (presetValue) => {
    update(presetValue)
    setOpen(false) // Close dropdown after selecting a preset
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-[180px] justify-between">
          {value === '' ? 'Select Day' : `${value} Day`}
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[180px]">
        <div className="p-2">
          <Input
            ref={inputRef}
            type="number"
            placeholder="0代表不限制"
            defaultValue={value === '' ? '' : String(value)} // Use defaultValue for uncontrolled input
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onClick={(e) => e.stopPropagation()} // Prevent closing dropdown on input click
            className="mb-2"
          />
        </div>
        <DropdownMenuSeparator />
        {daynOptions.map((option) => (
          <DropdownMenuItem
            key={option.code}
            onSelect={() => handlePresetSelect(option.code)}
          >
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default DropdownDay
